import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/ui-extra";
import { MessageSquare, RefreshCw, Phone, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { safeFormatDate, safeDistanceToNow } from "@/lib/dates";
import { cn } from "@/lib/utils";

/**
 * WhatsApp operator console. Lists every conversation the bot has had,
 * flags whether the number is bound to a staff record, and lets you drill
 * in to read the full message thread.
 */
export default function WhatsAppConsolePage() {
  const [status, setStatus] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [threadLoading, setThreadLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/whatsapp/status"),
        api.get("/whatsapp/conversations"),
      ]);
      setStatus(s.data);
      setConversations(c.data || []);
      // Auto-open the first conversation on initial load
      if (!active && c.data?.length) {
        openConversation(c.data[0]);
      }
    } finally { setLoading(false); }
    // openConversation is a stable local reference declared below; adding
    // it to deps would create a circular chain (openConversation → load →
    // openConversation) with no functional benefit. `active` is the only
    // value that actually affects the auto-open branch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const openConversation = async (conv) => {
    setActive(conv);
    setThreadLoading(true);
    try {
      const r = await api.get(`/whatsapp/conversations/${encodeURIComponent(conv.wa_id)}/messages`);
      setThread(r.data);
    } finally { setThreadLoading(false); }
  };

  // Silently re-fetch the active thread (no spinner) — used by the polling loop.
  const refreshActiveThread = useCallback(async () => {
    if (!active?.wa_id) return;
    try {
      const r = await api.get(`/whatsapp/conversations/${encodeURIComponent(active.wa_id)}/messages`);
      setThread(r.data);
    } catch (_e) { /* ignore transient errors during polling */ }
  }, [active]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 15s so new inbound messages appear without a manual click.
  // Pauses while a thread is actively loading to avoid clobbering it.
  useEffect(() => {
    const id = setInterval(() => {
      if (!threadLoading) {
        load();
        refreshActiveThread();
      }
    }, 15000);
    return () => clearInterval(id);
  }, [load, threadLoading, refreshActiveThread]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="WhatsApp Console"
        subtitle="Live view of staff conversations with the bot. bindings, message history, delivery status."
        actions={
          <Button variant="outline" onClick={load} data-testid="wa-refresh-btn" disabled={loading}>
            <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} /> Refresh
          </Button>
        }
      />

      {/* Health row */}
      <Card className="p-4 rounded-xl flex items-center gap-4 flex-wrap" data-testid="wa-status-card">
        <HealthChip ok={status?.configured}
                    label={status?.configured ? "Connected to Meta Cloud API" : "NOT configured"} />
        <Stat label="Phone number ID" value={status?.phone_number_id || ""} mono />
        <Stat label="Graph version" value={status?.graph_api_version || ""} mono />
        <Stat label="Total inbound" value={status?.inbound_count ?? 0} />
      </Card>

      <DiagnosticsPanel />

      {/* Split view */}
      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6">
        {/* Conversation list */}
        <Card className="rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b text-xs uppercase tracking-[0.14em] text-muted-foreground bg-[hsl(var(--ap-paper))]">
            Conversations ({conversations.length})
          </div>
          <div className="max-h-[640px] overflow-y-auto divide-y" data-testid="wa-convs">
            {loading && (
              <div className="p-8 flex justify-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            )}
            {!loading && conversations.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No conversations yet. Share <strong>+27 71 911 0938</strong> with staff and they can text <code>/help</code>.
              </div>
            )}
            {!loading && conversations.map((c) => (
              <button
                key={c.wa_id}
                onClick={() => openConversation(c)}
                data-testid={`wa-conv-${c.wa_id}`}
                className={cn(
                  "w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors",
                  active?.wa_id === c.wa_id && "bg-muted/60",
                )}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-medium truncate">{c.display_name}</span>
                  {c.bound
                    ? <Badge variant="outline" className="text-emerald-700 border-emerald-300 text-[10px]">staff</Badge>
                    : <Badge variant="outline" className="text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700 text-[10px]">unbound</Badge>}
                </div>
                <div className="text-xs text-muted-foreground truncate mt-0.5">
                  {c.last_text || `[${c.last_payload_type || "media"}]`}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-2">
                  <Phone className="h-3 w-3" />
                  <span className="font-mono">+{c.wa_id}</span>
                  <span>·</span>
                  <span>{c.last_received_at ? safeDistanceToNow(c.last_received_at, { addSuffix: true }) : ""}</span>
                  <span>·</span>
                  <span>{c.message_count} msg</span>
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Thread */}
        <Card className="rounded-xl overflow-hidden">
          {!active && (
            <div className="p-12 flex flex-col items-center justify-center text-muted-foreground gap-3">
              <MessageSquare className="h-10 w-10" />
              <div>Pick a conversation on the left to see the message history.</div>
            </div>
          )}
          {active && (
            <>
              <div className="px-5 py-3 border-b flex items-center justify-between bg-[hsl(var(--ap-paper))]">
                <div>
                  <div className="font-medium" data-testid="wa-thread-name">{active.display_name}</div>
                  <div className="text-xs text-muted-foreground font-mono">+{active.wa_id}
                    {active.staff_role && <span className="ml-2">· {active.staff_role}</span>}
                  </div>
                </div>
                {active.bound
                  ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-300 dark:hover:bg-emerald-900/40">Staff bound</Badge>
                  : <Badge variant="outline" className="text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700">Not registered</Badge>}
              </div>
              <div className="max-h-[640px] overflow-y-auto p-4 space-y-2 bg-[hsl(var(--ap-paper))]/40" data-testid="wa-thread">
                {threadLoading && (
                  <div className="flex justify-center py-6 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                )}
                {!threadLoading && (thread?.messages || []).slice().reverse().map((m) => (
                  <div
                    key={m.message_id}
                    data-testid={`wa-message-${m.message_id}`}
                    className="bg-muted text-foreground border border-border/60 rounded-lg px-3 py-2 max-w-[80%] shadow-sm"
                  >
                    <div className="text-sm whitespace-pre-wrap break-words">
                      {m.text || <em className="text-muted-foreground">[{m.payload_type || "media"}]</em>}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">
                      {safeFormatDate(m.received_at, "d MMM HH:mm", "")}
                    </div>
                  </div>
                ))}
                {!threadLoading && (!thread?.messages || thread.messages.length === 0) && (
                  <div className="text-center text-sm text-muted-foreground py-6">No messages yet.</div>
                )}
              </div>
              {/* Delivery status footer */}
              {thread?.statuses && thread.statuses.length > 0 && (
                <div className="px-5 py-3 border-t text-xs text-muted-foreground flex items-center gap-3 bg-[hsl(var(--ap-paper))]">
                  <span className="uppercase tracking-[0.14em]">Outbound:</span>
                  {(() => {
                    const counts = thread.statuses.reduce((acc, s) => {
                      acc[s.status] = (acc[s.status] || 0) + 1;
                      return acc;
                    }, {});
                    return Object.entries(counts).map(([k, v]) => (
                      <span key={k} className="flex items-center gap-1">
                        {k === "failed" ? <AlertCircle className="h-3 w-3 text-red-600" /> : <CheckCircle2 className="h-3 w-3 text-emerald-600" />}
                        {k}: <span className="font-mono">{v}</span>
                      </span>
                    ));
                  })()}
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

function HealthChip({ ok, label }) {
  return (
    <div className={cn(
      "inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium",
      ok ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
         : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    )} data-testid="wa-health-chip">
      <span className={cn("h-2 w-2 rounded-full", ok ? "bg-emerald-500" : "bg-amber-500")} />
      {label}
    </div>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div className="flex flex-col text-xs">
      <span className="uppercase tracking-[0.14em] text-muted-foreground">{label}</span>
      <span className={cn("text-sm mt-0.5", mono && "font-mono")}>{value}</span>
    </div>
  );
}


/**
 * On-demand end-to-end diagnostics so an admin can see — without log
 * access — exactly which step of the inbound→process→reply pipeline is
 * broken, and whether it's a PROCESS issue (our side) or DELIVERY
 * (Meta ↔ users). Includes a test-send to prove outbound independently.
 */
function DiagnosticsPanel() {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [testWa, setTestWa] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.get("/whatsapp/diagnostics");
      setData(r.data);
    } catch (e) {
      setData({ error: e?.response?.data?.detail || "Failed to run diagnostics" });
    } finally { setRunning(false); }
  };

  const testSend = async () => {
    if (!testWa.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post("/whatsapp/diagnostics/test-send", { wa_id: testWa.trim() });
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ sent: false, meta_response: e?.response?.data || "error" });
    } finally { setTesting(false); }
  };

  const sig = data?.step_2_signature?.last_check;
  const sigMismatch = sig && sig.ok === false && sig.recv_prefix && sig.expect_prefix;

  return (
    <Card className="p-4 rounded-xl space-y-4" data-testid="wa-diagnostics-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <AlertCircle className="h-4 w-4 text-[hsl(var(--ap-saddle))]" /> Connection diagnostics
        </div>
        <Button size="sm" variant="outline" onClick={run} disabled={running} data-testid="wa-run-diagnostics-btn">
          {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Run diagnostics
        </Button>
      </div>

      {data && !data.error && (
        <div className="space-y-3 text-sm" data-testid="wa-diagnostics-result">
          <div className="rounded-lg border bg-accent/40 p-3" data-testid="wa-diagnostics-verdict">
            <span className="font-medium">Verdict: </span>{data.verdict}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <DiagRow label="1 · Inbound received (Meta to us)"
                     ok={(data.step_1_inbound_delivery?.total_inbound_stored || 0) > 0}
                     detail={`${data.step_1_inbound_delivery?.total_inbound_stored ?? 0} stored · last ${data.step_1_inbound_delivery?.last_inbound?.received_at ? safeDistanceToNow(data.step_1_inbound_delivery.last_inbound.received_at) : "never"}`} />
            <DiagRow label="2 · Signature / App Secret"
                     ok={data.step_2_signature?.has_app_secret && sig?.ok !== false}
                     detail={!data.step_2_signature?.has_app_secret ? "App Secret missing"
                       : sig?.ok === false ? (sigMismatch ? "MISMATCH — wrong App Secret" : (sig?.header_present === false ? "signature header missing" : "failed"))
                       : sig?.ok === true ? "last inbound verified OK" : "no inbound checked yet"} />
            <DiagRow label="3 · Outbound token (us to Meta)"
                     ok={data.step_3_outbound_token?.ok}
                     detail={data.step_3_outbound_token?.ok
                       ? `valid · ${data.step_3_outbound_token?.phone?.display_phone_number || ""}`
                       : (data.step_3_outbound_token?.meta_error || data.step_3_outbound_token?.reason || "invalid/expired token")} />
            <DiagRow label="4 · Last reply attempt"
                     ok={data.step_4_last_outbound?.ok !== false}
                     detail={data.step_4_last_outbound?.at
                       ? `${data.step_4_last_outbound?.ok ? "sent" : "FAILED"} to ${data.step_4_last_outbound?.to || ""}`
                       : "no reply sent yet"} />
          </div>

          {sigMismatch && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs" data-testid="wa-sig-mismatch">
              <div className="font-medium text-destructive mb-1">App Secret mismatch confirmed</div>
              The signature on the last inbound message did not match. Received
              <code className="mx-1 px-1 rounded bg-muted">{sig.recv_prefix}…</code> but we computed
              <code className="mx-1 px-1 rounded bg-muted">{sig.expect_prefix}…</code>
              (secret fingerprint <code className="px-1 rounded bg-muted">{sig.secret_fp}</code>).
              The stored App Secret is not the one from the Meta app signing these webhooks — update it and re-publish.
            </div>
          )}

          <div className="flex items-end gap-2 flex-wrap pt-1">
            <div className="flex flex-col text-xs">
              <label className="uppercase tracking-[0.14em] text-muted-foreground mb-1">Test send to (wa id, e.g. 2771...)</label>
              <input value={testWa} onChange={(e) => setTestWa(e.target.value)}
                     placeholder="27829990000"
                     className="h-9 w-56 rounded-md border bg-background px-2 text-sm"
                     data-testid="wa-test-send-input" />
            </div>
            <Button size="sm" onClick={testSend} disabled={testing || !testWa.trim()} data-testid="wa-test-send-btn">
              {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Phone className="h-4 w-4 mr-2" />}
              Send test
            </Button>
            {testResult && (
              <span className={cn("text-xs", testResult.sent ? "text-emerald-600" : "text-destructive")} data-testid="wa-test-send-result">
                {testResult.sent ? "Meta accepted the message" : "Meta rejected — see details below"}
              </span>
            )}
          </div>
          {testResult && !testResult.sent && (
            <pre className="text-[11px] bg-muted rounded p-2 overflow-auto max-h-32" data-testid="wa-test-send-error">
              {JSON.stringify(testResult.meta_response, null, 2)}
            </pre>
          )}
        </div>
      )}
      {data?.error && <div className="text-sm text-destructive">{data.error}</div>}
      {!data && !running && (
        <p className="text-xs text-muted-foreground">Click "Run diagnostics" to check each step of the inbound to reply pipeline on the live server.</p>
      )}
    </Card>
  );
}

function DiagRow({ label, ok, detail }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border p-2.5">
      {ok ? <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-500 shrink-0" />
          : <AlertCircle className="h-4 w-4 mt-0.5 text-destructive shrink-0" />}
      <div className="min-w-0">
        <div className="text-xs font-medium">{label}</div>
        <div className="text-xs text-muted-foreground break-words">{detail}</div>
      </div>
    </div>
  );
}
